import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";
import { plural } from "@/lib/plural";

/**
 * The out-of-date state, drawn on both tabs off the served staleness: how
 * many positions are locked, that the results carry into claim strategy, and
 * that the analysis should be re-run before it is relied on. Only a completed
 * re-run clears it, so it stays while one is in motion, with its action closed.
 */
export function OutOfDate({
  stale,
  confirmedCount,
  running,
  starting,
  refusal = null,
  onRerun,
}: {
  stale: boolean;
  confirmedCount: number;
  running: boolean;
  starting: boolean;
  /** Why the server refused the last re-run, in its own words, or nothing. */
  refusal?: string | null;
  onRerun: () => void;
}) {
  if (!stale) return null;
  const busy = running || starting;
  return (
    <Panel
      data-testid="out-of-date"
      role="status"
      tone="warning"
      className="mt-6 flex flex-wrap items-center gap-3 text-[12.5px]"
    >
      <span className="flex-1">
        <strong>{plural(confirmedCount, "position")} locked.</strong> The
        results carry into claim strategy. The analysis is out of date and
        should be re-run before it is relied on.
      </span>
      <Button
        variant="secondary"
        size="dense"
        type="button"
        onClick={onRerun}
        disabled={busy}
        // The warning surface tints its own text, and a control on it has to
        // say it is a control rather than part of the sentence around it.
        className="border-warning-border"
      >
        <RefreshCw aria-hidden />
        {busy ? "Running…" : "Re-run the analysis"}
      </Button>
      {refusal !== null && !busy && (
        <p role="alert" className="basis-full text-destructive">
          It could not be re-run: {refusal}
        </p>
      )}
    </Panel>
  );
}
